// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  IAboutUsWorkCultureGetAllAttrs,
  IAboutUsWorkCultureGetAttrs,
  IAboutUsWorkCultureSaveAttrs,
  IAboutUsWorkCultureDeleteAttrs
} from 'interfaces/back-office/content-management/about-us/work-culture/about-us-work-culture-view.interface'
import {
  IAboutUsWorkCultureApiGetAllResponse,
  IAboutUsWorkCultureApiGetResponse,
  IAboutUsWorkCultureApiPaginatedResponse,
  IAboutUsWorkCultureApiSaveResponse,
  IAboutUsWorkCultureApiDeleteResponse
} from 'interfaces/back-office/content-management/about-us/work-culture/about-us-work-culture-api.interface'
import { IAboutUsWorkCultureDetail } from 'interfaces/back-office/content-management/about-us/work-culture'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import { dateUtils_formatDate } from 'utils/common/date.utils'

// About Us Work Culture Content Props
const aboutUsWorkCultureContentProps = {
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
}

// About Us WorkCulture Props
const aboutUsWorkCultureProps = {
  id: types.maybeNull(types.number),
  title: types.maybeNull(types.string),
  content: types.maybeNull(types.array(types.model('About Us Work Culture Content').props(aboutUsWorkCultureContentProps))),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// About Us WorkCulture List Model
const AboutUsWorkCultures = types
  .model('AboutUsWorkCultures')
  .props(withHelper_baseResponsePaginationTypes(types.model('AboutUsWorkCultureList').props(aboutUsWorkCultureProps)))

// About Us WorkCulture Detail Model
const AboutUsWorkCultureDetail = types.model('AboutUsWorkCultureDetail').props(aboutUsWorkCultureProps)

const AboutUsWorkCultureStoreModel = types
  .model('AboutUsWorkCulture')
  .props({
    aboutUsWorkCulture_isLoading: types.optional(types.boolean, false),
    aboutUsWorkCulture_isSaveLoading: types.optional(types.boolean, false),
    aboutUsWorkCulture_aboutUsWorkCultures: types.optional(AboutUsWorkCultures, {}),
    aboutUsWorkCulture_detail: types.optional(AboutUsWorkCultureDetail, {}),
    aboutUsWorkCulture_isDeleteLoading: types.optional(types.boolean, false)
  })
  .actions(self => ({
    aboutUsWorkCulture_SET_LOADING: (payload: boolean): void => {
      self.aboutUsWorkCulture_isLoading = payload
    },
    aboutUsWorkCulture_SET_SAVE_LOADING: (payload: boolean): void => {
      self.aboutUsWorkCulture_isSaveLoading = payload
    },
    aboutUsWorkCulture_SET_ABOUT_US_WORK_CULTURES: (payload: IAboutUsWorkCultureApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map(list => {
          return {
            ...list,
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.aboutUsWorkCulture_aboutUsWorkCultures = cast(mapPayload)
    },
    aboutUsWorkCulture_SET_ABOUT_US_WORK_CULTURE: (payload: IAboutUsWorkCultureDetail): void => {
      self.aboutUsWorkCulture_detail = cast(payload)
    },
    aboutUsWorkCulture_CLEAR_ABOUT_US_WORK_CULTURES: (): void => {
      self.aboutUsWorkCulture_aboutUsWorkCultures = cast({})
    },
    aboutUsWorkCulture_CLEAR_ABOUT_US_WORK_CULTURE: (): void => {
      self.aboutUsWorkCulture_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch about Us WorkCultures
     *
     * @param {IAboutUsWorkCultureGetAllAttrs} payload
     *
     * @return {any} any
     */
    aboutUsWorkCulture_fetchAboutUsWorkCultures: flow(function* (payload: IAboutUsWorkCultureGetAllAttrs) {
      self.aboutUsWorkCulture_SET_LOADING(true)

      const response: IAboutUsWorkCultureApiGetAllResponse = yield self.environment.aboutUsWorkCultureApi.aboutUsWorkCulture_getAboutUsWorkCultures({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) self.aboutUsWorkCulture_SET_ABOUT_US_WORK_CULTURES(response.res.data)

      self.aboutUsWorkCulture_SET_LOADING(false)
    }),

    /**
     * @description Fetch about us work-culture
     *
     * @param {IAboutUsWorkCultureGetAttrs} payload
     *
     * @return {any} any
     */
    aboutUsWorkCulture_fetchAboutUsWorkCulture: flow(function* (payload: IAboutUsWorkCultureGetAttrs) {
      self.aboutUsWorkCulture_SET_LOADING(true)

      const response: IAboutUsWorkCultureApiGetResponse = yield self.environment.aboutUsWorkCultureApi.aboutUsWorkCulture_getAboutUsWorkCulture({
        params: { id: payload.params.id },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.aboutUsWorkCulture_SET_ABOUT_US_WORK_CULTURE(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.aboutUsWorkCulture_SET_LOADING(false)
    }),

    /**
     * @description Save about us work-culture
     *
     * @param {IAboutUsWorkCultureSaveAttrs} payload
     *
     * @return {any} any
     */
    aboutUsWorkCulture_saveAboutUsWorkCulture: flow(function* (payload: IAboutUsWorkCultureSaveAttrs) {
      self.aboutUsWorkCulture_SET_SAVE_LOADING(true)

      const response: IAboutUsWorkCultureApiSaveResponse = yield self.environment.aboutUsWorkCultureApi.aboutUsWorkCulture_saveAboutUsWorkCulture({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsWorkCulture_SET_SAVE_LOADING(false)
    }),

    aboutUsWorkCulture_deleteAboutUsWorkCulture: flow(function* (payload: IAboutUsWorkCultureDeleteAttrs) {
      self.aboutUsWorkCulture_SET_LOADING(true)
      const response: IAboutUsWorkCultureApiDeleteResponse = yield self.environment.aboutUsWorkCultureApi.aboutUsWorkCulture_deleteAboutUsWorkCulture({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig }
      })

      if (response.ok) {
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.aboutUsWorkCulture_SET_LOADING(false)
    })
  }))

  export const createAboutUsWorkCultureStoreDefaultModel = () => types.optional(AboutUsWorkCultureStoreModel, {})